import React, { useEffect, useState } from "react";
import { Grid, CircularProgress, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  callAPI,
  Method,
  IsSmallScreen,
  updateMainHeader,
} from "../../commonFunctions/CommonFunctions";
import WSDataGrid, { IColumns } from "../../Components/DataGrid/WSDataGrid";
import WSMobileDataGridAdvanced, {
  IOrderColumns,
} from "../../Components/DataGrid/WSMobileDataGridAdvanced";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import ConfirmDialog, {
  useConfirmDialog,
} from "../../Components/Confirmation/ConfirmDialog";
import PermissionDenied from "../../commonFunctions/PermissionDenied";
import { AllPermissions } from "../../commonFunctions/PermissionManger";

interface IVendorPayment {
  paymentID: string;
  vendorID: string;
  vendorName?: string;
  billIDs: string[];
  paymentDate: string;
  paymentMode: string;
  amountPaid: number;
  referenceNo: string;
}

export const VendorPaymentList: React.FC<any> = React.memo(() => {
  const { view, add, edit, remove } = AllPermissions.getRolePermission;
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();
  const isMobile = IsSmallScreen();

  const [payments, setPayments] = useState<IVendorPayment[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    open,
    title,
    onConfirm,
    handleClose,
    openConfirmDialog,
  } = useConfirmDialog();

  useEffect(() => {
    updateMainHeader("Vendor Payments");
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res: any = await callAPI("VendorPayment", Method.GET);
      const data = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];

      // ✅ Normalize paymentID to avoid undefined issue
      setPayments(
        data.map((p: any) => ({
          ...p,
          paymentID: p.paymentID || p.id || p._id,
        }))
      );
    } catch (err) {
      console.error("Error fetching payments:", err);
      showMessage("Failed to fetch payments", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const res: any = await callAPI(`VendorPayment`, Method.DELETE,null,id);

      if (res && (res.success || res.status === 200)) {
        showMessage("Deleted successfully", "success");
        fetchData();
      } else {
        showMessage(res?.message || "Failed to delete", "error");
      }
    } catch (err) {
      console.error("Error deleting payment:", err);
      showMessage("Error deleting payment", "error");
    } finally {
      handleClose();
    }
  };

  const handleAction = (action: string, row: IVendorPayment) => {
  console.log("Row data:", row);
  console.log("Row payment id:", row.paymentID);


  
  const id = (row as any).paymentID || (row as any).id || (row as any)._id;

  // if (!id) {
  //   console.error("Payment ID not found for this row:", row);
  //   return;
  // }

  if (action === "Edit") {
    navigate(`/vendorpayment/edit/${id}`);
  } else if (action === "Delete" && remove) {
    openConfirmDialog("Delete this Payment?", () => handleDelete(id));
  }
};


  const handleAdd = () => navigate("/vendorpayment/add");

  const columns: IColumns[] = [
    { header: "Payment Date", fieldName: "paymentDate", type: "date" },
    { header: "Mode", fieldName: "paymentMode", type: "default" },
    { header: "Amount Paid", fieldName: "amountPaid", type: "default" },
    { header: "Reference No", fieldName: "referenceNo", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: [edit ? "Edit" : undefined, remove ? "Delete" : undefined].filter(Boolean) as string[],
      actionCallback: handleAction,
    },
  ];

  const mobColumns: IOrderColumns[] = [
    { header: "Vendor", fieldName: "vendorName", type: "default" },
    { header: "Amount Paid", fieldName: "amountPaid", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: [edit ? "Edit" : undefined, remove ? "Delete" : undefined].filter(Boolean) as string[],
      actionCallback: handleAction,
    },
  ];

  return (
    <Grid container spacing={2}>
      {view ? (
        <Grid item xs={12}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="60vh"
            >
              <CircularProgress />
            </Box>
          ) : !isMobile ? (
            <WSDataGrid
              id="vendorPaymentGrid"
              title="Vendor Payments"
              columns={columns}
              source={{
                filter: [],
                page: 10,
                pageSize: 1000,
                sort: [],
                url: "VendorPayment/search",
              }}
              // ✅ Keep the original built-in searchBox
              searchBox={{ fieldName: "referenceNo" }}
              addButton={
                add ? { label: "New Payment", color: "secondary" } : undefined
              }
              onAddButtonClick={handleAdd}
            />
          ) : (
            <WSMobileDataGridAdvanced
              title="Vendor Payments"
              id="vendorPaymentGrid"
              columns={mobColumns}
              data={payments}
              addButton={
                add ? { label: "New Payment", color: "secondary" } : undefined
              }
              onAddButtonClick={handleAdd}
            />
          )}
        </Grid>
      ) : (
        <PermissionDenied />
      )}
      <ConfirmDialog
        open={open}
        title={title}
        onConfirm={onConfirm}
        onClose={handleClose}
      />
    </Grid>
  );
});
